import {Component, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {Router} from '@angular/router';
import {CompanyService} from '@services/company.service';
import {ICompanyDetailsSimplified} from '@models/company.interface';
import {forkJoin} from 'rxjs';
import {ICompteAdministration} from '@models/compteAdministration.interface';

type FiscalStatus = 'missing' | 'pending' | 'submitted' | 'validated' | 'late';

interface ClientFiscalStatus {
  siren: string;
  name: string;
  cfe: FiscalStatus;
  taxeFonciere: FiscalStatus;
  tva: FiscalStatus;
  impotSociete: FiscalStatus;
}

@Component({
  selector: 'app-expert-dashboard',
  templateUrl: './expert-dashboard.html',
})
export class ExpertDashboard implements OnInit {
  private readonly companyService = inject(CompanyService);
  private readonly router = inject(Router);

  companies: WritableSignal<ICompanyDetailsSimplified[]> = signal([]);
  clientStatuses: WritableSignal<ClientFiscalStatus[]> = signal([]);
  loading = signal(true);

  ngOnInit(): void {
    this.companyService.getCompanies().subscribe({
      next: (companies) => {
        this.companies.set(companies);
        if (!companies.length) {
          this.clientStatuses.set([]);
          this.loading.set(false);
          return;
        }

        forkJoin(companies.map((company) => this.companyService.getCompanyBySiret(company.siret))).subscribe({
          next: (details) => {
            this.clientStatuses.set(details.map((detail, index) => this.mapClientStatus(companies[index], detail)));
            this.loading.set(false);
          },
          error: () => {
            this.clientStatuses.set(companies.map((company) => ({
              siren: company.siret.slice(0, 9),
              name: company.nomEntreprise,
              cfe: 'missing',
              taxeFonciere: 'missing',
              tva: 'missing',
              impotSociete: 'missing',
            })));
            this.loading.set(false);
          },
        });
      },
      error: () => {
        this.companies.set([]);
        this.clientStatuses.set([]);
        this.loading.set(false);
      },
    });
  }

  openClients(): void {
    void this.router.navigate(['/dashboard', 'expert', 'clients']);
  }

  openCertifications(): void {
    void this.router.navigate(['/dashboard', 'expert', 'suivi-fiscal']);
  }

  activeCompaniesCount(): number {
    return this.companies().filter((company) => company.active).length;
  }

  closedCompaniesCount(): number {
    return this.companies().filter((company) => !company.active).length;
  }

  openPortal(): void {
    void this.router.navigate(['/dashboard', 'expert', 'portail-echange']);
  }

  openDocuments(): void {
    void this.router.navigate(['/dashboard', 'expert', 'documents']);
  }

  openNotifications(): void {
    void this.router.navigate(['/dashboard', 'expert', 'notifications']);
  }

  openSettings(): void {
    void this.router.navigate(['/dashboard', 'expert', 'parametres']);
  }

  cfeOkCount(): number {
    return this.clientStatuses().filter((client) => this.isOkStatus(client.cfe)).length;
  }

  taxeFonciereOkCount(): number {
    return this.clientStatuses().filter((client) => this.isOkStatus(client.taxeFonciere)).length;
  }

  tvaOkCount(): number {
    return this.clientStatuses().filter((client) => this.isOkStatus(client.tva)).length;
  }

  impotSocieteOkCount(): number {
    return this.clientStatuses().filter((client) => this.isOkStatus(client.impotSociete)).length;
  }

  visibleClientStatuses(): ClientFiscalStatus[] {
    return this.clientStatuses().slice(0, 10);
  }

  statusLabel(status: FiscalStatus): string {
    return {
      missing: 'Manquant',
      pending: 'En attente',
      submitted: 'Soumis',
      validated: 'Validé',
      late: 'En retard',
    }[status];
  }

  statusClass(status: FiscalStatus): string {
    return {
      missing: 'text-red-600 border-red-300',
      pending: 'text-amber-600 border-amber-300',
      submitted: 'text-blue-600 border-blue-300',
      validated: 'text-green-600 border-green-300',
      late: 'text-red-600 border-red-300',
    }[status];
  }

  private mapClientStatus(company: ICompanyDetailsSimplified, detail: ICompteAdministration): ClientFiscalStatus {
    const entries = detail.company?.taxCalendar ?? [];
    return {
      siren: company.siret.slice(0, 9),
      name: company.nomEntreprise || company.siret,
      cfe: this.mapStatus(entries.find((entry) => this.matchesCategory(entry.declarationType, 'cfe'))?.etatPeriode),
      taxeFonciere: this.mapStatus(entries.find((entry) => this.matchesCategory(entry.declarationType, 'tf'))?.etatPeriode),
      tva: this.mapStatus(entries.find((entry) => this.matchesCategory(entry.declarationType, 'tva'))?.etatPeriode),
      impotSociete: this.mapStatus(entries.find((entry) => this.matchesCategory(entry.declarationType, 'is'))?.etatPeriode),
    };
  }

  private matchesCategory(value: string | null | undefined, category: 'cfe' | 'tf' | 'tva' | 'is'): boolean {
    const label = (value ?? '').toLowerCase();
    if (category === 'cfe') return label.includes('cfe');
    if (category === 'tf') return label.includes('fonci');
    if (category === 'tva') return label.includes('tva');
    return label.includes('soc') || label.includes('is');
  }

  private mapStatus(value: string | null | undefined): FiscalStatus {
    const label = (value ?? '').toLowerCase();
    if (!label) return 'missing';
    if (label.includes('retard')) return 'late';
    if (label.includes('sold') || label.includes('valid')) return 'validated';
    if (label.includes('soumis') || label.includes('déposé') || label.includes('depose')) return 'submitted';
    return 'pending';
  }

  private isOkStatus(status: FiscalStatus): boolean {
    return status === 'validated' || status === 'submitted';
  }
}
